import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../commons/utils';
import { THEME_V2_CLASS } from '@v2/constants';
import type { CommonProps } from '../types';

type DividerOrientation = 'horizontal' | 'vertical';
type DividerVariant = 'solid' | 'dashed' | 'dotted';

const dividerVariants = cva('shrink-0 border-0 border-border-primary', {
  variants: {
    orientation: {
      horizontal: 'w-full h-0',
      vertical: 'h-full w-0',
    },
    variant: {
      solid: '',
      dashed: '',
      dotted: '',
    },
  },
  compoundVariants: [
    {
      orientation: 'horizontal',
      variant: 'solid',
      class: 'border-t border-solid',
    },
    {
      orientation: 'horizontal',
      variant: 'dashed',
      class:
        'h-px bg-[repeating-linear-gradient(to_right,var(--color-border-primary)_0_6px,transparent_6px_10px)]',
    },
    {
      orientation: 'horizontal',
      variant: 'dotted',
      class:
        'h-px bg-[repeating-linear-gradient(to_right,var(--color-border-primary)_0_1px,transparent_1px_4px)]',
    },
    {
      orientation: 'vertical',
      variant: 'solid',
      class: 'border-r border-solid',
    },
    {
      orientation: 'vertical',
      variant: 'dashed',
      class:
        'w-px bg-[repeating-linear-gradient(to_bottom,var(--color-border-primary)_0_6px,transparent_6px_10px)]',
    },
    {
      orientation: 'vertical',
      variant: 'dotted',
      class:
        'w-px bg-[repeating-linear-gradient(to_bottom,var(--color-border-primary)_0_1px,transparent_1px_4px)]',
    },
  ],
});

type BaseProps = {
  /** Direction of the divider line
   * @default 'horizontal'
   */
  orientation?: DividerOrientation;
  /** Line style of the divider
   * @default 'solid'
   */
  variant?: DividerVariant;
  /**
   * When true, the divider is purely visual and uses role="none".
   * When false, it uses role="separator" for assistive tech.
   * @default true
   */
  decorative?: boolean;
} & CommonProps;

export type DividerProps = BaseProps &
  Omit<React.ComponentPropsWithoutRef<'div'>, keyof BaseProps> & {
    /** Ref forwarded to the underlying HTML element */
    ref?: React.Ref<HTMLDivElement>;
  };

const Divider = ({
  ref,
  orientation = 'horizontal',
  variant = 'solid',
  decorative = true,
  className,
  testId,
  theme,
  ...props
}: DividerProps) => {
  return (
    <div
      {...props}
      ref={ref}
      role={decorative ? 'none' : 'separator'}
      aria-orientation={decorative ? undefined : orientation}
      data-testid={testId}
      className={cn(
        THEME_V2_CLASS,
        theme,
        dividerVariants({ orientation, variant }),
        className
      )}
    />
  );
};

Divider.displayName = 'Divider';

export { Divider };
